import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { 
    Package, Plus, Search, Edit2, Trash2, MapPin, Tag, Box, 
    ArrowRightLeft, History, Clock, List, CheckCircle2, 
    ShieldAlert, Wrench, XCircle, QrCode, Cpu, ChevronRight, X, TrendingUp
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useDevice } from '../hooks/useDevice';
import { toast } from 'react-hot-toast';

// --- SUB-COMPONENTS ---

const HistoryModal = ({ instance, onClose }) => {
    const { data: history, isLoading } = useQuery({
        queryKey: ['inventory-history', instance.id],
        queryFn: async () => (await client.get(`/inventory/instances/${instance.id}/history`)).data,
        enabled: !!instance
    });

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4 italic">
            <div className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-[2.5rem] shadow-2xl p-8 flex flex-col max-h-[80vh]">
                <div className="flex justify-between items-center mb-8">
                    <h4 className="text-xl md:text-2xl font-black text-white uppercase flex items-center gap-3 italic tracking-tighter">
                        <History className="w-6 h-6 text-blue-500" />
                        История ТМЦ
                    </h4>
                    <button onClick={onClose} className="p-3 bg-neutral-800 rounded-xl text-neutral-500 hover:text-white transition-all">
                        <XCircle size={20} />
                    </button>
                </div>
                <div className="overflow-y-auto custom-scrollbar flex-1 space-y-4 pr-2">
                    {isLoading ? <div className="text-center text-neutral-500 py-10 opacity-50 animate-pulse uppercase text-[10px] font-black">Загрузка логов...</div> :
                     history?.length === 0 ? <div className="text-center text-neutral-500 py-10 italic opacity-20 uppercase text-[10px] font-black">Логи пусты</div> :
                     history?.map((entry) => (
                        <div key={entry.id} className="bg-neutral-950 border border-neutral-800 p-5 rounded-[1.5rem] relative overflow-hidden group">
                            <div className="flex justify-between items-start mb-3 relative z-10">
                                <span className="text-blue-500 font-bold text-[9px] bg-blue-500/5 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-blue-500/10">{entry.action}</span>
                                <span className="text-neutral-600 text-[9px] font-black">{new Date(entry.timestamp).toLocaleString()}</span>
                            </div>
                            <p className="text-neutral-300 text-xs font-bold mb-3 relative z-10">{entry.details}</p>
                            <p className="text-neutral-500 text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 relative z-10 op-60"><Box className="w-3 h-3 text-blue-500/50" /> {entry.user_name}</p>
                            <div className="absolute -right-4 -bottom-4 w-12 h-12 bg-blue-600 opacity-5 blur-2xl group-hover:opacity-10 transition-opacity" />
                        </div>
                     ))}
                </div>
            </div>
        </div>
    );
};

const TransferModal = ({ instance, users, onClose, queryClient }) => {
    const [toUserId, setToUserId] = useState('');
    const [newLocation, setNewLocation] = useState(instance.location || '');
    const transferMutation = useMutation({
        mutationFn: (data) => client.post(`/inventory/instances/${instance.id}/transfer`, data),
        onSuccess: () => { queryClient.invalidateQueries(['inventory']); onClose(); toast.success('Актив успешно передан'); }
    });
    const handleSubmit = (e) => { e.preventDefault(); transferMutation.mutate({ to_user_id: toUserId, new_location: newLocation }); };
    
    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[250] flex items-center justify-center p-4 italic">
            <div className="bg-neutral-900 border border-neutral-800 w-full max-w-sm rounded-[3rem] shadow-3xl p-10 animate-in zoom-in-95 duration-300 relative overflow-hidden">
                <h4 className="text-2xl font-black text-white uppercase tracking-tighter mb-2 flex items-center gap-3 italic leading-none">
                    <ArrowRightLeft className="w-6 h-6 text-blue-500" />
                    Передать
                </h4>
                <p className="text-[9px] font-black text-blue-500/50 uppercase tracking-[0.2em] mb-10 italic">Inventory Transfer Protocol</p>
                <div className="bg-neutral-950 p-4 rounded-2xl border border-neutral-800 mb-8">
                     <p className="text-[10px] font-black text-neutral-600 uppercase mb-1">Идентификатор</p>
                     <p className="text-white font-mono font-bold">{instance.identifier || `#${instance.id}`}</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block italic">Новый ответственный</label>
                        <select required value={toUserId} onChange={e => setToUserId(e.target.value)} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold appearance-none">
                            <option value="" disabled>Выберите из списка...</option>
                            {users?.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block italic">Место назначения</label>
                        <input type="text" value={newLocation} onChange={e => setNewLocation(e.target.value)} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 transition-all font-bold" />
                    </div>
                    <div className="flex gap-3 mt-10">
                        <button type="button" onClick={onClose} className="flex-1 py-5 bg-neutral-800 hover:bg-neutral-700 text-neutral-500 hover:text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all">Отмена</button>
                        <button type="submit" disabled={transferMutation.isPending} className="flex-1 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all shadow-xl shadow-blue-500/20">
                            {transferMutation.isPending ? 'Запись...' : 'OK'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const GroupInstancesModal = ({ group, onClose, queryClient, users, isMobile }) => {
    const [selectedInstance, setSelectedInstance] = useState(null);
    const [activeModal, setActiveModal] = useState(null); // 'edit', 'transfer', 'history', 'qr'
    const [isWritingNfc, setIsWritingNfc] = useState(false);
    const instances = group.instances || [];

    const handleWriteNfc = async (inst) => {
        if (!('NDEFReader' in window)) {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            toast.error(isIOS ? "Блокировка NFC в Safari. Используйте Chrome/Android." : "NFC не поддерживается.");
            return;
        }
        try {
            setIsWritingNfc(true);
            const ndef = new NDEFReader();
            await ndef.scan();
            const identifier = inst.identifier || `INST-${inst.id}`;
            const universalLink = `https://t.me/BroBrandERP_bot/app?startapp=${identifier}`;
            await ndef.write(universalLink);
            toast.success("Чип запрограммирован!");
        } catch (error) { toast.error("Сбой NFC записи"); }
        finally { setIsWritingNfc(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center md:items-center justify-center md:p-4 italic">
            <div className={`bg-neutral-950 border border-neutral-800 w-full max-w-4xl shadow-3xl overflow-hidden flex flex-col transition-all duration-500 ${isMobile ? 'h-[94dvh] rounded-t-[3rem] mt-auto' : 'max-h-[85vh] rounded-[3.5rem]'}`}>
                <div className="p-8 md:p-12 flex flex-col h-full overflow-hidden">
                    <div className="flex justify-between items-start mb-10">
                        <div>
                            <h3 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tighter flex items-center gap-4 italic leading-none">
                                <Box className="w-10 h-10 text-blue-600" />
                                {group.name}
                            </h3>
                            <p className="text-neutral-500 text-[10px] font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-3 italic whitespace-nowrap overflow-hidden text-ellipsis">Управление единицами оборудования</p>
                        </div>
                        <button onClick={onClose} className="p-4 bg-neutral-900 rounded-2xl text-neutral-500 hover:text-white transition-all shadow-xl">
                            <X size={24}/>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-2">
                        {instances.length === 0 ? (
                            <div className="py-20 text-center opacity-10 flex flex-col items-center gap-4">
                                <Package size={48} />
                                <p className="text-xs font-black uppercase tracking-widest">Список пуст</p>
                            </div>
                        ) : (
                            instances.map(inst => (
                                <div key={inst.id} className="bg-neutral-900 border border-neutral-800/50 p-6 rounded-[2rem] md:rounded-[2.5rem] space-y-6 transition-all hover:bg-neutral-900/80 group">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                            <button onClick={() => { setSelectedInstance(inst); setActiveModal('qr'); }} className="bg-neutral-950 px-4 py-2 rounded-xl border border-neutral-800 flex items-center gap-3 hover:border-blue-500 transition-all">
                                                <QrCode size={14} className="text-blue-500" />
                                                <code className="text-white font-mono font-bold text-[10px]">{inst.identifier || `#${inst.id}`}</code>
                                            </button>
                                            <button 
                                                onClick={() => handleWriteNfc(inst)} 
                                                disabled={isWritingNfc} 
                                                className={`p-2 rounded-xl border transition-all ${!('NDEFReader' in window) ? 'bg-neutral-800 border-neutral-700 opacity-20 cursor-not-allowed' : 'bg-neutral-800 border-neutral-700 text-neutral-500 hover:text-emerald-500 hover:border-emerald-500 shadow-xl'}`}
                                            >
                                                <Cpu className={`w-4 h-4 ${isWritingNfc ? 'animate-pulse text-emerald-500' : ''}`} />
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-3 py-1.5 rounded-xl text-[8px] font-black uppercase tracking-widest border ${inst.status === 'в наличии' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : inst.status === 'в ремонте' ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/20'}`}>
                                                {inst.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 items-center">
                                        <div className="bg-neutral-950/40 p-4 rounded-2xl border border-neutral-800/40">
                                            <p className="text-[8px] font-black text-neutral-600 uppercase tracking-widest mb-1 italic">Локация</p>
                                            <p className="text-[11px] text-white font-bold truncate leading-none">{inst.location || group.location || '—'}</p>
                                        </div>
                                        <div className="bg-neutral-950/40 p-4 rounded-2xl border border-neutral-800/40">
                                            <p className="text-[8px] font-black text-neutral-600 uppercase tracking-widest mb-1 italic">Владение</p>
                                            <p className="text-[11px] text-zinc-400 font-black uppercase tracking-widest leading-none truncate">{users?.find(u => u.id === inst.responsible_id)?.name || 'Million Connections'}</p>
                                        </div>
                                        <div className="col-span-2 flex justify-end gap-3 pt-3 flex-wrap">
                                            <button 
                                                onClick={() => { setSelectedInstance(inst); setActiveModal('transfer'); }} 
                                                className="px-6 py-3 bg-blue-600/10 text-blue-500 rounded-[1.2rem] font-black uppercase text-[9px] tracking-widest hover:bg-blue-600 hover:text-white transition-all border border-blue-500/20 flex items-center justify-center gap-2"
                                            >
                                                <ArrowRightLeft size={14}/> Передать
                                            </button>
                                            <button 
                                                onClick={() => { setSelectedInstance(inst); setActiveModal('history'); }} 
                                                className="w-12 h-12 bg-neutral-800 hover:bg-white hover:text-black rounded-[1.2rem] text-neutral-500 transition-all flex items-center justify-center shadow-xl"
                                            >
                                                <History size={16}/>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {activeModal === 'qr' && selectedInstance && (
                <div className="fixed inset-0 z-[300] bg-black/98 backdrop-blur-3xl flex flex-col items-center justify-center p-8" onClick={() => setActiveModal(null)}>
                    <div className="bg-white p-12 rounded-[4rem] flex flex-col items-center gap-10 shadow-3xl animate-in zoom-in-75 duration-500" onClick={e => e.stopPropagation()}>
                        <div className="bg-neutral-100 p-8 rounded-[2.5rem] shadow-inner">
                            <QRCodeSVG value={selectedInstance.identifier || `INST-${selectedInstance.id}`} size={isMobile ? 220 : 300} level="H" includeMargin />
                        </div>
                        <div className="text-center font-black uppercase italic tracking-tighter text-neutral-900 text-3xl">
                             {selectedInstance.identifier || `#${selectedInstance.id}`}
                        </div>
                    </div>
                    <p className="mt-10 text-white/40 text-[10px] font-black uppercase tracking-[0.4em] italic">Million Connections Identity Framework</p>
                </div>
            )}
            {activeModal === 'transfer' && selectedInstance && <TransferModal instance={selectedInstance} users={users} onClose={() => setActiveModal(null)} queryClient={queryClient} />}
            {activeModal === 'history' && selectedInstance && <HistoryModal instance={selectedInstance} onClose={() => setActiveModal(null)} />}
        </div>
    );
};

// --- MAIN PAGE ---

const Inventory = () => {
    const queryClient = useQueryClient();
    const { isMobile } = useDevice();
    const [searchTerm, setSearchTerm] = useState('');
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [viewingGroup, setViewingGroup] = useState(null);
    const [formData, setFormData] = useState({ name: '', quantity: '0', unit: 'шт', price: '0', location: '' });

    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: async () => (await client.get('/users')).data
    });

    const { data: groups, isLoading } = useQuery({
        queryKey: ['inventory'],
        queryFn: async () => (await client.get('/inventory')).data
    });

    const createMutation = useMutation({
        mutationFn: (data) => client.post('/inventory', data),
        onSuccess: () => { queryClient.invalidateQueries(['inventory']); setIsGroupModalOpen(false); resetForm(); toast.success('Категория добавлена'); }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => client.patch(`/inventory/${id}`, data),
        onSuccess: () => { queryClient.invalidateQueries(['inventory']); setIsGroupModalOpen(false); resetForm(); toast.success('Данные синхронизированы'); }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/inventory/${id}`),
        onSuccess: () => { queryClient.invalidateQueries(['inventory']); toast.success('Группа удалена'); }
    });

    const resetForm = () => { setFormData({ name: '', quantity: '0', unit: 'шт', price: '0', location: '' }); setEditingGroup(null); };

    const handleEditGroup = (group) => {
        setEditingGroup(group);
        setFormData({ name: group.name, quantity: String(group.quantity), unit: group.unit, price: String(group.price), location: group.location || '' });
        setIsGroupModalOpen(true);
    };

    const handleSubmitGroup = (e) => {
        e.preventDefault();
        const data = { ...formData, quantity: parseFloat(formData.quantity) || 0, price: parseFloat(formData.price) || 0 };
        if (editingGroup) updateMutation.mutate({ id: editingGroup.id, data });
        else createMutation.mutate(data);
    };

    const filteredGroups = groups?.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase())) || [];
    const totalValue = filteredGroups.reduce((sum, g) => sum + (g.price * g.quantity), 0);
    const totalItems = filteredGroups.reduce((sum, g) => sum + g.quantity, 0);

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">ТМЦ Активы</h1>
                    <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">Million Connections Inventory :: Assets Center</p>
                </div>
                <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                     <button 
                        onClick={() => { resetForm(); setIsGroupModalOpen(true); }} 
                        className="w-full md:w-auto bg-white text-black px-8 md:px-10 py-5 rounded-2xl md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-widest hover:bg-neutral-200 transition-all shadow-xl flex items-center justify-center gap-3 italic"
                    >
                        <Plus size={20}/> {isMobile ? 'Добавить категорию' : 'Создать группу активов'}
                    </button>
                </div>
            </header>

            {/* Stats Dashboard */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-8">
                {[
                    { label: 'Капитализация', val: `${totalValue.toLocaleString()} ₽`, icon: <TrendingUp/>, col: 'emerald' },
                    { label: 'Общий фонд', val: totalItems, icon: <Package/>, col: 'blue' },
                    { label: 'Хранилища', val: filteredGroups.length, icon: <Box/>, col: 'amber', hideMobile: true }
                ].filter(s => !isMobile || !s.hideMobile).map((s, i) => (
                    <div key={i} className="bg-neutral-900 border border-neutral-800 p-4 md:p-8 rounded-2xl md:rounded-[2.5rem] flex items-center gap-4 md:gap-8 shadow-2xl relative overflow-hidden group">
                        <div className={`w-12 h-12 md:w-20 md:h-20 bg-${s.col}-500/10 text-${s.col}-500 rounded-xl md:rounded-[1.5rem] flex items-center justify-center border border-${s.col}-500/20 shadow-xl z-10 transition-transform group-hover:rotate-6`}>
                            {React.cloneElement(s.icon, { size: isMobile ? 24 : 36 })}
                        </div>
                        <div className="relative z-10">
                            <p className="text-[7px] md:text-[10px] font-black text-neutral-500 uppercase tracking-widest leading-none mb-1 md:mb-2">{s.label}</p>
                            <p className="text-sm md:text-3xl font-black text-white italic truncate">{s.val}</p>
                        </div>
                        <div className={`absolute -right-6 -bottom-6 w-24 h-24 bg-${s.col}-500/5 blur-3xl rounded-full`} />
                    </div>
                ))}
            </div>

            {/* Responsive Search */}
            <div className="relative group">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600 transition-colors group-focus-within:text-blue-500" />
                <input 
                    type="text" 
                    placeholder="Найти категорию ТМЦ..." 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="w-full bg-neutral-900 border border-neutral-800 text-white pl-16 pr-6 py-5 md:py-6 rounded-[2rem] text-sm font-bold outline-none focus:border-blue-500 shadow-2xl transition-all" 
                />
            </div>

            {/* List/Table Unified Content */}
            <div className="relative min-h-[400px]">
                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3].map(i => <div key={i} className="h-64 bg-neutral-900/50 rounded-[3rem] border border-neutral-800 animate-pulse" />)}
                    </div>
                ) : filteredGroups.length === 0 ? (
                    <div className="py-24 text-center opacity-20 flex flex-col items-center gap-6">
                        <Package size={64} />
                        <p className="text-xs font-black uppercase tracking-[0.4em]">Реестр активов пуст</p>
                    </div>
                ) : (
                    <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'gap-6 md:gap-8'} lg:grid-cols-2 xl:grid-cols-3`}>
                        {filteredGroups.map(group => (
                            <div 
                                key={group.id} 
                                onClick={() => setViewingGroup(group)}
                                className="group relative bg-neutral-900 border border-neutral-800 p-8 rounded-[2.5rem] md:rounded-[3rem] text-left active:scale-[0.98] md:hover:border-blue-500/50 transition-all cursor-pointer shadow-2xl flex flex-col min-h-[220px]"
                            >
                                <div className="flex justify-between items-start mb-6">
                                    <div className="p-4 bg-neutral-950 border border-neutral-800 rounded-2xl text-blue-500 shadow-inner group-hover:scale-105 transition-all">
                                        <Package size={28}/>
                                    </div>
                                    <div className="bg-neutral-950 border border-neutral-800 px-4 py-2 rounded-xl text-[10px] font-black italic">
                                        <span className="text-blue-500">{group.quantity}</span> <span className="text-neutral-500">{group.unit}</span>
                                    </div>
                                </div>
                                <h3 className="text-xl md:text-2xl font-black text-white italic uppercase tracking-tighter leading-none mb-3 truncate pr-4">{group.name}</h3>
                                <div className="flex items-center gap-2 text-neutral-600 text-[10px] font-black uppercase tracking-widest italic mb-6">
                                    <MapPin size={12} className="text-blue-500/50" /> {group.location || 'База'}
                                </div>
                                <div className="mt-auto pt-6 border-t border-white/5 flex justify-between items-center">
                                     <div className="flex flex-col">
                                         <p className="text-[8px] font-black text-neutral-700 uppercase tracking-widest mb-1 leading-none">Капитал</p>
                                         <p className="text-xs font-black text-white italic">{(group.price * group.quantity).toLocaleString()} ₽</p>
                                     </div>
                                     <div className="flex gap-2">
                                         <button onClick={(e) => { e.stopPropagation(); handleEditGroup(group); }} className="w-10 h-10 bg-neutral-800 text-neutral-500 hover:text-white rounded-xl flex items-center justify-center transition-all">
                                             <Edit2 size={16}/>
                                         </button>
                                         <button onClick={(e) => { e.stopPropagation(); if(window.confirm(`Удалить группу "${group.name}"?`)) deleteMutation.mutate(group.id); }} className="w-10 h-10 bg-rose-500/5 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl flex items-center justify-center transition-all border border-rose-500/10">
                                             <Trash2 size={16}/>
                                         </button>
                                         <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-xl shadow-blue-500/20 md:opacity-0 md:group-hover:opacity-100 transition-all">
                                              <ChevronRight size={18}/>
                                         </div>
                                     </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals Section */}
            {isGroupModalOpen && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-2xl z-[200] flex items-center justify-center p-4">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-[3rem] md:rounded-[4rem] p-8 md:p-12 shadow-3xl flex flex-col italic relative overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-10 relative z-10">
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black text-white italic uppercase tracking-tighter leading-none">
                                    {editingGroup ? 'Правка Категории' : 'Новый Резерв'}
                                </h3>
                                <p className="text-[10px] text-blue-500 font-bold uppercase tracking-widest mt-2 italic md:block hidden">Million Connections Asset Entry</p>
                            </div>
                            <button onClick={() => setIsGroupModalOpen(false)} className="w-12 h-12 md:w-16 md:h-16 bg-neutral-800 rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={handleSubmitGroup} className="space-y-8 relative z-10">
                            <div>
                                <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Название актива / Группы</label>
                                <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl md:rounded-[2rem] px-6 py-5 text-white font-black md:text-xl outline-none focus:border-blue-500 transition-all italic" />
                            </div>
                            <div className="grid grid-cols-2 gap-4 md:gap-6">
                                {!editingGroup && (
                                    <div>
                                        <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-2 mb-2 block italic">Базовый объем</label>
                                        <input type="number" required value={formData.quantity} onChange={e => setFormData({...formData, quantity: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl p-5 text-white font-black outline-none focus:border-blue-500 transition-all font-mono" />
                                    </div>
                                )}
                                <div>
                                    <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block italic">Цена покупки (ед.)</label>
                                    <input type="number" required value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl p-5 text-emerald-400 font-black outline-none focus:border-blue-500 transition-all" />
                                </div>
                            </div>
                            <div>
                                 <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block italic">Локация хранения</label>
                                 <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl p-5 text-white font-bold outline-none focus:border-blue-500 transition-all italic" placeholder="Основной офис / Склад" />
                            </div>
                            <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="w-full bg-blue-600 text-white py-6 md:py-8 rounded-[2rem] md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-[0.2em] shadow-2xl shadow-blue-500/20 active:scale-95 transition-all mt-4 italic">
                                {createMutation.isPending || updateMutation.isPending ? 'Запись данных...' : 'Зафиксировать категорию'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
            
            {viewingGroup && <GroupInstancesModal group={viewingGroup} onClose={() => setViewingGroup(null)} queryClient={queryClient} users={users} isMobile={isMobile} />}
        </div>
    );
};

export default Inventory;
